import React, { useState, useEffect, useMemo } from 'react';
import {
    TextField,
    Tooltip,
    Button,
    Box,
    Checkbox,
    FormControlLabel,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import Constants from '../../../assests/Constants/constants';
import defaultAccountCards from '../../../assests/Constants/defaultAccountCards';
import { toast } from 'react-toastify';
import { useDirectionByLanguage } from '../../../hooks/useDirectionByLanguage';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import PCNDocumentationModal from '../../PCN/PCNDocumentationModal';

const AccountantDetailsModals = ({
    isPopupOpen,
    value,
    handleValueChange,
    isEditMode,
    handleSaveChange: parentHandleSaveChange,
    handleClose,
    contentKey,
    //custome data option
    formattedSortCodesGroupNamesOptions,
    formattedSortCodeVatType,
    formattedPalCodeCategoryOptions,
    formattedPalCodeAccountingGroupOptions,
    filteredCode6111Options = [],
    filteredCode6111OptionsForSortCode = [],
    onPalCodeTypeChange, // Callback to trigger Code6111 fetch


    existingOtherDocumentsType = [],
    existingSortCodes = [],
    existingSortCodeGroups = [],
    existingPalCodes = [],
    existingPalCodeCategories = [],
    existingAccountingGroups = [],
}) => {
    const { i18n, t } = useTranslation();
    const direction = useDirectionByLanguage(i18n);
    const [showPcnDocumentation, setShowPcnDocumentation] = useState(false);
    // Turns on inline red validation messages once the user tries to save.
    const [showValidation, setShowValidation] = useState(false);

    // Reset validation highlighting whenever the modal (re)opens or switches type.
    useEffect(() => {
        setShowValidation(false);
    }, [isPopupOpen, contentKey]);

    // Small reusable red helper shown under a required field when it is empty.
    const RequiredError = ({ show }) =>
        show ? (
            <div
                style={{
                    color: '#d32f2f',
                    fontSize: '12px',
                    marginTop: '4px',
                    textAlign: direction === 'ltr' ? 'left' : 'right',
                }}
            >
                {t('ThisFieldIsRequired') || 'This field is required'}
            </div>
        ) : null;

    
    // -----------------------------
    // Helper to check required fields
    // -----------------------------
    const isSaveDisabled = useMemo(() => {
        const val = value || {};

        switch (contentKey) {
            case 'OtherDocumentsType':
                // Document Type is required => we check `value`
                return !val; // or (!val.trim()) if it's always a string

            case 'SortingCode':
                // Required fields: sortCodeId, sortCodeName, sortCodeVatType, P&L, VAT.
                // When participating in P&L, sortCodeGroup is also required.
                return (
                    !val.sortCodeId ||
                    !val.sortCodeName ||
                    !val.sortCodeVatType ||
                    val.participatesInProfitAndLoss == null ||
                    val.vat == null ||
                    (val.participatesInProfitAndLoss === true && !val.sortCodeGroup)
                );

            case 'SortingCodeGroup':
                // Required field: sortCodeGroupName
                return (
                    !val.sortCodeGroupListNumber ||
                    !val.sortCodeGroupName
                );

            case 'PalCode':
                return (
                    !val.palCodeType ||
                    !val.palCodeId ||
                    !val.palCodeCategory
                );

            case 'PalCodeCategory':
                return (
                    !val.palCodeCategoryId ||
                    !val.orderNumber ||
                    !val.accountingGroup 
                );
            case 'AccountingGroup':
                return (
                    !val.accountingGroupClassifaction ||
                    !val.accountingGroupName ||
                    !val.orderNumber
                );

            default:
                // If we don't recognize the contentKey, do not disable
                return false;
        }
    }, [contentKey, value]);


    /**
  * Auto-increment logic:
  * When the modal opens in "New" mode, find the last ID/number/serial, increment it by 1, 
  * and put it in the form. 
  * The user can still override it manually afterward.
  */

    useEffect(() => {
        if (!isEditMode && isPopupOpen) {
            switch (contentKey) {

                case 'SortingCode': {
                    const lastCodeId = existingSortCodes && existingSortCodes.length
                        ? Math.max(...existingSortCodes.map((item) => Number(item.sortCodeId) || 0))
                        : 0;
                    handleValueChange({
                        ...value,
                        sortCodeId: String(lastCodeId + 1),
                        // Set participatesInProfitAndLoss to false if no sort code group is selected
                        participatesInProfitAndLoss: value.sortCodeGroup ? value.participatesInProfitAndLoss : false,
                    });
                    break;
                }
                case 'SortingCodeGroup': {
                    const numericListNumbers = existingSortCodeGroups ? existingSortCodeGroups.map((item) => parseInt(item.sortCodeListNumber, 10) || 0) : [];
                    const maxNum = numericListNumbers.length ? Math.max(...numericListNumbers) : 0;
                    handleValueChange({
                        ...value,
                        sortCodeGroupListNumber: (maxNum + 1).toString()
                    });
                    break;
                }
                case 'PalCode': {
                    // Auto-increment orderNumber for PalCode
                    const lastPalCodeOrder = existingPalCodes && existingPalCodes.length
                        ? Math.max(...existingPalCodes.map((item) => Number(item.orderNumber) || 0))
                        : 0;
                    handleValueChange((prev) => ({
                        ...prev,
                        orderNumber: lastPalCodeOrder + 1,
                    }));
                    break;
                }
                case 'PalCodeCategory': {

                    // Same for orderNumber if you want to auto-increment:
                    const lastOrderNumber = existingPalCodeCategories && existingPalCodeCategories.length
                        ? Math.max(...existingPalCodeCategories.map((item) => Number(item.orderNumber) || 0))
                        : 0;
                    handleValueChange((prev) => ({
                        ...prev,
                        orderNumber: lastOrderNumber + 1,
                    }));
                    break;
                }
                case 'AccountingGroup': {
                    // for "orderNumber"
                    const lastOrder = existingAccountingGroups && existingAccountingGroups.length
                        ? Math.max(...existingAccountingGroups.map((item) => Number(item.orderNumber) || 0))
                        : 0;
                    handleValueChange({
                        ...value,
                        orderNumber: lastOrder + 1,
                    });
                    break;
                }
                default:
                    break;
            }
        }
    }, [isEditMode, isPopupOpen, contentKey]);

    /**
     * Validation logic: 
     *  - Check if the numeric/ID you care about already exists in the relevant array.
     *  - If it does, show an error and prevent saving.
     */
    const checkForDuplicates = () => {
        switch (contentKey) {
            case 'OtherDocumentsType': {
                // Example check: does the same "Document Type" text exist?
                // Adjust to match how your "OtherDocumentsType" is stored 
                const isDuplicate = existingOtherDocumentsType.some(
                    (item) => item.name?.trim() === value?.trim(),
                );
                if (isDuplicate) {
                    toast.info(t('DocumentTypeAlreadyExists'));
                    return true;
                }
                break;
            }
            case 'SortingCode': {
                // Check `sortCodeId` and `sortCodeName` uniqueness
                const isDuplicate = existingSortCodes.some((item) => {

                    // Skip the current item if in edit mode and ID matches
                    if (isEditMode && String(item.id) === String(value.id)) return false;

                    const itemSortCodeId = item.sortCodeId ? String(item.sortCodeId).trim() : "";
                    const valueSortCodeId = value.sortCodeId ? String(value.sortCodeId).trim() : "";
                    const originalSortCodeId = value.originalSortCodeId ? String(value.originalSortCodeId).trim() : "";

                    const itemSortCodeName = item.sortCodeName ? item.sortCodeName.trim().toLowerCase() : "";
                    const valueSortCodeName = value.sortCodeName ? value.sortCodeName.trim().toLowerCase() : "";

                    // Check if `sortCodeId` is changed and exists in another item
                    if (valueSortCodeId !== originalSortCodeId) {
                        if (existingSortCodes.some((existing) => String(existing.sortCodeId).trim() === valueSortCodeId)) {
                            toast.info(t('SortCodeIdAlreadyExists'));
                            return true;
                        }
                    }

                    // If `sortCodeId` is unchanged, check if `sortCodeName` is a duplicate
                    if (existingSortCodes.some((existing) => String(existing.sortCodeName).trim().toLowerCase() === valueSortCodeName)) {
                        toast.info(t('SortCodeNameAlreadyExists'));
                        return true;
                    }

                    return false;
                });

                if (isDuplicate) {
                    return true;
                }
                break;
            }
            case 'SortingCodeGroup': {
                // Check `sortCodeGroupListNumber` and `sortCodeGroupName` uniqueness
                const isDuplicate = existingSortCodeGroups.some((item) => {

                    if (isEditMode) return false;


                    return (
                        Number(item.sortCodeGroupListNumber) === Number(value.sortCodeGroupListNumber) ||
                        item.sortCodeGroup.trim().toLowerCase() === value.sortCodeGroupName.trim().toLowerCase()
                    );
                });

                if (isDuplicate) {
                    toast.info(t('GroupListNumberAlreadyExists'));
                    return true;
                }
                break;
            }

            case 'PalCode': {
                // Check `palCodeId`
                const isDuplicate = existingPalCodes.some((item) => {

                    if (isEditMode) return false;
                    return String(item.palCodeId).trim() === String(value.palCodeId).trim();
                });
                if (isDuplicate) {
                    toast.info(t('BalanceClassificationNameAlreadyExists'));
                    return true;
                }
                break;
            }
            case 'PalCodeCategory': {
                // Check `palCodeCategoryId` or also `orderNumber` if you want
                const catDuplicate = existingPalCodeCategories.some((item) => {
                    if (isEditMode) return false;
                    return String(item.palCodeCategoryId).trim() === String(value.palCodeCategoryId).trim();
                });
                if (catDuplicate) {
                    toast.info(t('BalanceClassificationCategoryAlreadyExists'));
                    return true;
                }
                // Also check orderNumber if you want it unique
                const orderDuplicate = existingPalCodeCategories.some((item) => {
                    if (isEditMode) return false;
                    return Number(item.orderNumber) === Number(value.orderNumber);
                });
                if (orderDuplicate) {
                    toast.info(t('SerialOrderNumberAlreadyExists'));
                    return true;
                }
                break;
            }
            case 'AccountingGroup': {
                // Typically might check `orderNumber` uniqueness
                const isDuplicate = existingAccountingGroups.some((item) => {
                    if (isEditMode) return false;
                    return Number(item.orderNumber) === Number(value.orderNumber);
                });
                if (isDuplicate) {
                    toast.info(t('SerialNumberExistsInAccountingGroups'));
                    return true;
                }
                break;
            }
            default:
                break;
        }
        // No duplicates found
        return false;
    };


    /**
   * Wrap your parent handleSaveChange in a local check:
   */
    const handleSaveChange = () => {
        // Surface the red required-field messages instead of silently doing nothing.
        if (isSaveDisabled) {
            setShowValidation(true);
            return;
        }
        const hasDuplicates = checkForDuplicates();
        if (hasDuplicates) {
            // Stop here if there is a duplicate
            return;
        }
        // Otherwise, proceed with your parent's save logic
        parentHandleSaveChange();
    };

    const customStyles = {
        container: (provided) => ({
            ...provided,
            width: '100% !important',
            // Anchor the menu to this select — do not portal (portal + RTL flex
            // row misplaces the menu under the sibling field).
            position: 'relative',
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '48px',
            width: '100% !important',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: 'none',
            boxShadow: 'none',
            padding: '0 10px',
            textAlign: 'left',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black'
        }),
        singleValue: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        menu: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
            position: 'absolute',
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 30,
        }),
        menuList: (provided) => ({
            ...provided,
            maxHeight: 220,
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#F9AA2A'
                : state.isFocused
                    ? '#ebebeb'
                    : null,
            color: 'black',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? 'darkgray' : 'black',
            '&:hover': {
                color: 'darkgray',
            },
        }),
    };

    // In-place menus (no portal) so each dropdown stays under its own control
    // inside the RTL two-column layout.
    const selectCommonProps = {
        styles: customStyles,
        classNamePrefix: 'accountant-select',
        menuShouldScrollIntoView: false,
        isRtl: direction === 'rtl',
    };

    const contentConfig = {
        OtherDocumentsType: {
            headerContent: (
                <h2 className='centered-heading'>
                    {isEditMode ? t("Edit OtherDocumentsType") : t("New OtherDocumentsType")}
                </h2>
            ),
            bodyContent: (
                <div>
                    <div className="form-row-item-title">
                        {t('Document Type')}<span className="required-asterisk">*</span>
                    </div>
                    <TextField
                        fullWidth
                        placeholder={t('Document Type')}
                        value={value}
                        onChange={(e) => handleValueChange(e.target.value)}
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                            sx: {
                                height: '48px',
                                padding: '0 10px',
                                borderRadius: '8px',
                                background: '#F5F5F6',
                                border: '1px solid transparent',
                                '&:focus-within': {
                                    border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                },
                            },
                        }}
                        inputProps={{
                            style: {
                                textAlign: direction === 'ltr' ? 'left' : 'right',
                            },
                        }}
                    />
                    <RequiredError show={showValidation && !value} />
                </div>
            ),
        },
        SortingCode: {
            headerContent: (
                <h2 className='centered-heading'>
                    {isEditMode ? t("EditSortCode") : t("NewSortCode")}
                    {isEditMode && value?.isEditable === false && (
                        <span style={{ color: 'red', fontSize: '14px', fontWeight: 400 }}> - {t('NotEditable')}</span>
                    )}
                </h2>
            ),
            bodyContent: ({ value, handleValueChange, direction }) => {
                const sortCodeReadOnly = isEditMode && value?.isEditable === false;

                // Responsive two-column rows that never collapse (EditModal-style).
                const rowStyle = {
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: '20px',
                    marginTop: '16px',
                };
                const itemStyle = { flex: '1 1 200px', minWidth: 0 };

                // Renders a Debit/Credit account picker: choose from the default
                // account cards (accountCards.csv) or type any free numeric value.
                const renderAccountAutocomplete = (fieldKey) => {
                    const rawValue = value?.[fieldKey] ?? '';
                    const selectedValue =
                        defaultAccountCards.find(
                            (card) => String(card.cardId) === String(rawValue),
                        ) || (rawValue ? String(rawValue) : null);

                    return (
                        <Autocomplete
                            freeSolo
                            fullWidth
                            selectOnFocus
                            clearOnBlur={false}
                            handleHomeEndKeys
                            autoHighlight
                            disabled={sortCodeReadOnly}
                            options={defaultAccountCards}
                            value={selectedValue}
                            filterOptions={(options, state) => {
                                const q = state.inputValue.trim().toLowerCase();
                                if (!q) return options;
                                return options.filter(
                                    (o) =>
                                        String(o.cardId).toLowerCase().includes(q) ||
                                        o.name.toLowerCase().includes(q),
                                );
                            }}
                            getOptionLabel={(option) =>
                                typeof option === 'string'
                                    ? option
                                    : `${option.cardId} - ${option.name}`
                            }
                            isOptionEqualToValue={(option, val) =>
                                String(option.cardId) ===
                                String(typeof val === 'string' ? val : val?.cardId)
                            }
                            onChange={(event, newValue) => {
                                const nextValue =
                                    newValue == null
                                        ? ''
                                        : typeof newValue === 'string'
                                            ? newValue.replace(/\D/g, '')
                                            : String(newValue.cardId);
                                handleValueChange({ ...value, [fieldKey]: nextValue });
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'input') return;
                                // Keep only digits so the account stays a valid number.
                                handleValueChange({
                                    ...value,
                                    [fieldKey]: newInputValue.replace(/\D/g, ''),
                                });
                            }}
                            // Keep the list anchored under this field inside the Dialog (RTL-safe).
                            componentsProps={{
                                popper: {
                                    placement: direction === 'rtl' ? 'bottom-end' : 'bottom-start',
                                    style: { zIndex: 14000 },
                                    modifiers: [
                                        { name: 'offset', options: { offset: [0, 4] } },
                                        {
                                            name: 'preventOverflow',
                                            options: { boundary: 'viewport', padding: 8 },
                                        },
                                    ],
                                },
                            }}
                            renderOption={(props, option) => (
                                <li
                                    {...props}
                                    key={option.cardId}
                                    style={{
                                        ...(props.style || {}),
                                        textAlign: direction === 'rtl' ? 'right' : 'left',
                                        direction,
                                    }}
                                >
                                    {`${option.cardId} - ${option.name}`}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={t(
                                        fieldKey === 'sortCodeDebit'
                                            ? 'Debit Account'
                                            : 'Credit Account',
                                    )}
                                    variant="standard"
                                    InputProps={{
                                        ...params.InputProps,
                                        disableUnderline: true,
                                        sx: {
                                            minHeight: '48px',
                                            padding: '0 10px',
                                            borderRadius: '8px',
                                            background: '#F5F5F6',
                                            border: '1px solid transparent',
                                            '&:focus-within': {
                                                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                            },
                                        },
                                    }}
                                    inputProps={{
                                        ...params.inputProps,
                                        inputMode: 'numeric',
                                        style: {
                                            ...(params.inputProps?.style || {}),
                                            textAlign: direction === 'ltr' ? 'left' : 'right',
                                        },
                                    }}
                                />
                            )}
                        />
                    );
                };

                const autocompleteFieldSx = {
                    minHeight: '48px',
                    padding: '0 10px',
                    borderRadius: '8px',
                    background: '#F5F5F6',
                    border: '1px solid transparent',
                    '&:focus-within': {
                        border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                    },
                };

                // Simple option picker (Yes/No, VAT %, …) using MUI Autocomplete so
                // the list anchors to the field correctly inside the RTL Dialog.
                const renderSelectAutocomplete = ({
                    options,
                    value: selected,
                    onChange,
                    placeholder,
                    disabled = false,
                    disableClearable = true,
                }) => (
                    <Autocomplete
                        fullWidth
                        options={options}
                        value={selected}
                        disabled={disabled}
                        disableClearable={disableClearable}
                        openOnFocus
                        autoHighlight
                        getOptionLabel={(option) => option?.label ?? ''}
                        isOptionEqualToValue={(option, val) =>
                            option?.value === val?.value
                        }
                        onChange={(_event, newValue) => {
                            onChange(newValue);
                        }}
                        componentsProps={{
                            popper: {
                                placement: direction === 'rtl' ? 'bottom-end' : 'bottom-start',
                                style: { zIndex: 14000 },
                                modifiers: [
                                    { name: 'offset', options: { offset: [0, 4] } },
                                    {
                                        name: 'preventOverflow',
                                        options: { boundary: 'viewport', padding: 8 },
                                    },
                                    {
                                        name: 'flip',
                                        options: { fallbackPlacements: ['top-end', 'top-start'] },
                                    },
                                    // Match the open menu width to the input field width.
                                    {
                                        name: 'sameWidth',
                                        enabled: true,
                                        phase: 'beforeWrite',
                                        requires: ['computeStyles'],
                                        fn: ({ state }) => {
                                            state.styles.popper.width = `${state.rects.reference.width}px`;
                                        },
                                        effect: ({ state }) => {
                                            state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
                                        },
                                    },
                                ],
                            },
                            paper: {
                                sx: {
                                    direction,
                                    textAlign: direction === 'rtl' ? 'right' : 'left',
                                    width: '100%',
                                    minWidth: '100%',
                                    boxSizing: 'border-box',
                                },
                            },
                        }}
                        renderOption={(props, option) => (
                            <li
                                {...props}
                                key={String(option.value)}
                                style={{
                                    ...(props.style || {}),
                                    textAlign: direction === 'rtl' ? 'right' : 'left',
                                    direction,
                                }}
                            >
                                {option.label}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={placeholder}
                                variant="standard"
                                InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                    sx: autocompleteFieldSx,
                                }}
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: true,
                                    style: {
                                        ...(params.inputProps?.style || {}),
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                        cursor: 'pointer',
                                    },
                                }}
                            />
                        )}
                    />
                );

                const participatesOptions = [
                    { label: t('Yes'), value: true },
                    { label: t('No'), value: false },
                ];
                const vatOptions = Constants.VATPercentage.map((v) => ({
                    label: `${v}%`,
                    value: v,
                }));

                return (
                <div className={`${direction}`} style={{ width: '100%', height: '100%' }} >
                    {/* SortingCode ID */}
                    <div className="form-row-accountant" style={{ ...rowStyle, marginTop: 0 }}>
                        <div className="form-row-item" style={itemStyle}>
                            <div className="form-row-item-title">
                                {t('SortCode Id')}<span className="required-asterisk">*</span>
                            </div>
                            <TextField
                                fullWidth
                                disabled={sortCodeReadOnly}
                                placeholder={t('SortCode Id')}
                                value={value?.sortCodeId || ''}
                                onChange={(e) => handleValueChange({ ...value, sortCodeId: e.target.value })}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                        height: '48px',
                                        padding: '0 10px',
                                        borderRadius: '8px',
                                        background: '#F5F5F6',
                                        border: '1px solid transparent',
                                        '&:focus-within': {
                                            border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                        },
                                    },
                                }}
                                inputProps={{
                                    style: {
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                    },
                                }}
                            />
                            <RequiredError show={showValidation && !value?.sortCodeId} />
                        </div>
                        {/* SortCode Name */}
                        <div className="form-row-item" style={itemStyle}>
                            <div className="form-row-item-title">
                                {t('SortCode Name')}<span className="required-asterisk">*</span>
                            </div>
                            <TextField
                                fullWidth
                                disabled={sortCodeReadOnly}
                                placeholder={t('SortCode Name')}
                                value={value.sortCodeName || ''}
                                onChange={(e) => handleValueChange({ ...value, sortCodeName: e.target.value })}
                                variant="standard"
                                InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                        height: '48px',
                                        padding: '0 10px',
                                        borderRadius: '8px',
                                        background: '#F5F5F6',
                                        border: '1px solid transparent',
                                        '&:focus-within': {
                                            border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                        },
                                    },
                                }}
                                inputProps={{
                                    style: {
                                        textAlign: direction === 'ltr' ? 'left' : 'right',
                                    },
                                }}
                            />
                            <RequiredError show={showValidation && !value?.sortCodeName} />
                        </div>
                    </div>
                    {/* SortCode Group — only when participates in P&L is Yes (or unset); when No, group is cleared and sent as null.
                        Always editable, even when the sort code itself is not editable. */}
                    <div className="form-row-accountant" style={rowStyle}>
                        {value.participatesInProfitAndLoss !== false && (
                        <div className="form-row-item" style={itemStyle}>
                            <div className="form-row-item-title">
                                {t('SelectSortCodeGroupName')}
                                {value.participatesInProfitAndLoss === true && (
                                    <span className="required-asterisk">*</span>
                                )}
                            </div>
                            <Select
                                placeholder={t('SelectSortCodeGroupName')}
                                options={
                                    value.participatesInProfitAndLoss === true
                                        ? formattedSortCodesGroupNamesOptions
                                        : [
                                            { label: t('Clear'), value: null },
                                            ...formattedSortCodesGroupNamesOptions
                                        ]
                                }
                                isSearchable={true}
                                value={formattedSortCodesGroupNamesOptions.find(item => item.value === value.sortCodeGroup) || null}
                                onChange={(selectedOption) => {
                                    const newValue = { ...value, sortCodeGroup: selectedOption?.value || null };
                                    if (!selectedOption?.value) {
                                        newValue.participatesInProfitAndLoss = false;
                                    }
                                    handleValueChange(newValue);
                                }}
                                isClearable={false}
                                {...selectCommonProps}
                            />
                            <RequiredError
                                show={
                                    showValidation &&
                                    value.participatesInProfitAndLoss === true &&
                                    !value?.sortCodeGroup
                                }
                            />
                        </div>
                        )}

                        {/* SortCode VAT Type */}
                        <div className="form-row-item" style={itemStyle}>
                            <div className="form-row-item-title">
                                {t('SelectSortCodeVatType')}<span className="required-asterisk">*</span>
                            </div>
                            <Select
                                placeholder={t('SelectSortCodeVatType')}
                                options={formattedSortCodeVatType}
                                isSearchable={true}
                                isDisabled={sortCodeReadOnly}
                                value={formattedSortCodeVatType.find(item => item.value === value.sortCodeVatType) || null}
                                onChange={(selectedOption) => handleValueChange({ ...value, sortCodeVatType: selectedOption.value })}
                                {...selectCommonProps}
                            />
                            <RequiredError show={showValidation && !value?.sortCodeVatType} />
                        </div>
                    </div>
                    {/* Debit Account */}
                    <div className="form-row-accountant" style={rowStyle}>
                        <div className="form-row-item" style={itemStyle}>
                            <Tooltip title={t('AutoSelectAccountCardWithSortCode')} arrow placement="top">
                                <div className="form-row-item-title">
                                    {t('Debit Account')}
                                </div>
                            </Tooltip>
                            {renderAccountAutocomplete('sortCodeDebit')}
                        </div>

                        {/* Credit Account */}
                        <div className="form-row-item" style={itemStyle}>
                            <Tooltip title={t('AutoSelectAccountCardWithSortCode')} arrow placement="top">
                                <div className="form-row-item-title">
                                    {t('Credit Account')}
                                </div>
                            </Tooltip>
                            {renderAccountAutocomplete('sortCodeCredit')}
                        </div>
                    </div>
                    <div className="form-row-accountant" style={rowStyle}>
                        {/* Participates in Profit and Loss */}
                        <div className="form-row-item" style={itemStyle}>
                            <div className="form-row-item-title">
                                {t('participatesInProfitAndLoss')}<span className="required-asterisk">*</span>
                            </div>
                            {renderSelectAutocomplete({
                                options: participatesOptions,
                                placeholder: t('participatesInProfitAndLoss'),
                                disabled: sortCodeReadOnly,
                                value:
                                    value.participatesInProfitAndLoss === true
                                        ? participatesOptions[0]
                                        : value.participatesInProfitAndLoss === false
                                            ? participatesOptions[1]
                                            : null,
                                onChange: (selectedOption) => {
                                    if (!selectedOption) return;
                                    const participates = selectedOption.value;
                                    handleValueChange({
                                        ...value,
                                        participatesInProfitAndLoss: participates,
                                        ...(participates === false ? { sortCodeGroup: null } : {}),
                                    });
                                },
                            })}
                            <RequiredError show={showValidation && value?.participatesInProfitAndLoss == null} />
                        </div>

                        {/* VAT - always editable, even when the sort code itself is not editable */}
                        <div className="form-row-item" style={itemStyle}>
                            <div className="form-row-item-title">
                                {t('VAT')}<span className="required-asterisk">*</span>
                            </div>
                            {renderSelectAutocomplete({
                                options: vatOptions,
                                placeholder: t('VAT'),
                                value: vatOptions.find((option) => option.value === value.vat) || null,
                                onChange: (selectedOption) => {
                                    if (!selectedOption) return;
                                    handleValueChange({ ...value, vat: selectedOption.value });
                                },
                            })}
                            <RequiredError show={showValidation && value?.vat == null} />
                        </div>
                    </div>

                    {/* PCN Transaction Type */}
                    <div className="form-row-accountant" style={rowStyle}>
                        <div className="form-row-item" style={{ flex: '1 1 100%', minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <div className="form-row-item-title">
                                    {t('PcnTransactionType')}
                                </div>
                                <Tooltip title={t('ViewPcnDocumentation')} arrow placement="top">
                                    <Button
                                        size="small"
                                        startIcon={<InfoIcon />}
                                        onClick={() => setShowPcnDocumentation(true)}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '12px',
                                            padding: '2px 8px',
                                            minWidth: 'auto'
                                        }}
                                    >
                                        {t('PCNDocumentation')}
                                    </Button>
                                </Tooltip>
                            </Box>
                            <Select
                                placeholder={t('SelectPcnTransactionType')}
                                options={[
                                    { label: t('Default') + ' (Auto)', value: null },
                                    { label: 'S - ' + t('RegularSale'), value: 'S' },
                                    { label: 'L - ' + t('PrivateCustomer'), value: 'L' },
                                    { label: 'M - ' + t('SelfInvoiceSale'), value: 'M' },
                                    { label: 'Y - ' + t('ExportSale'), value: 'Y' },
                                    { label: 'I - ' + t('PalestinianCustomer'), value: 'I' },
                                    { label: 'T - ' + t('RegularInput'), value: 'T' },
                                    { label: 'K - ' + t('PettyCash'), value: 'K' },
                                    { label: 'R - ' + t('ImportInput'), value: 'R' },
                                    { label: 'P - ' + t('PalestinianSupplier'), value: 'P' },
                                    { label: 'H - ' + t('OtherDocument'), value: 'H' },
                                    { label: 'C - ' + t('SelfInvoiceInput'), value: 'C' }
                                ]}
                                isSearchable={true}
                                isClearable={true}
                                value={
                                    value.pcnTransactionType 
                                        ? { label: value.pcnTransactionType, value: value.pcnTransactionType }
                                        : { label: t('Default') + ' (Auto)', value: null }
                                }
                                onChange={(selectedOption) =>
                                    handleValueChange({ ...value, pcnTransactionType: selectedOption?.value || null })
                                }
                                {...selectCommonProps}
                            />
                        </div>
                    </div>

                    {/* Inventory Type (opening / closing inventory) */}
                    <div className="form-row-accountant" style={rowStyle}>
                        <div className="form-row-item profile-full-width" style={{ flex: '1 1 100%', minWidth: 0 }}>
                            <div className="form-row-item-title">
                                {t('inventoryType')}
                            </div>
                            <Select
                                placeholder={t('inventoryType')}
                                options={[
                                    { label: t('inventoryNone'), value: 0 },
                                    { label: t('inventoryOpening'), value: 1 },
                                    { label: t('inventoryClosing'), value: 2 }
                                ]}
                                isSearchable={false}
                                value={
                                    [
                                        { label: t('inventoryNone'), value: 0 },
                                        { label: t('inventoryOpening'), value: 1 },
                                        { label: t('inventoryClosing'), value: 2 }
                                    ].find(option => option.value === (Number(value.inventoryType) || 0)) || null
                                }
                                onChange={(selectedOption) =>
                                    handleValueChange({ ...value, inventoryType: selectedOption?.value ?? 0 })
                                }
                                {...selectCommonProps}
                            />
                        </div>
                    </div>

                    {/* Code6111 */}
                    <div className="form-row-accountant" style={rowStyle}>
                        <div className="form-row-item profile-full-width" style={{ flex: '1 1 100%', minWidth: 0 }}>
                            <div className="form-row-item-title">
                                {t('Code6111')}
                            </div>
                            <Select
                                placeholder={t('SelectCode6111')}
                                options={(filteredCode6111OptionsForSortCode || []).sort((a, b) => {
                                    const numA = Number(a.number) || 0;
                                    const numB = Number(b.number) || 0;
                                    return numA - numB;
                                }).map((item) => ({
                                    label: `${item.number} - ${item.name}`,
                                    value: item.id,
                                    number: item.number,
                                    name: item.name,
                                    title: item.title,
                                }))}
                                isSearchable={true}
                                value={(filteredCode6111OptionsForSortCode || []).sort((a, b) => {
                                    const numA = Number(a.number) || 0;
                                    const numB = Number(b.number) || 0;
                                    return numA - numB;
                                }).map((item) => ({
                                    label: `${item.number} - ${item.name}`,
                                    value: item.id,
                                })).find(item => item.value === (value.code6111Id || value.Code6111Id)) || null}
                                onChange={(selectedOption) => handleValueChange({ ...value, code6111Id: selectedOption?.value || null })}
                                {...selectCommonProps}
                                isClearable={true}
                            />
                        </div>
                    </div>

                    {/* Apply the new VAT and accounts to existing transactions */}
                    {isEditMode && !sortCodeReadOnly && (
                    <div className="form-row-accountant" style={rowStyle}>
                        <div className="form-row-item profile-full-width" style={{ flex: '1 1 100%', minWidth: 0 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={value.updateTransactions || false}
                                        onChange={(e) => handleValueChange({ ...value, updateTransactions: e.target.checked })}
                                        sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
                                    />
                                }
                                label={t('UpdateTransactionsWithSortCode')}
                                sx={{
                                    margin: 0,
                                    '& .MuiFormControlLabel-label': { fontSize: '14px' },
                                }}
                            />
                        </div>
                    </div>
                    )}

                </div>
                );
            },
        },
        SortingCodeGroup: {
            headerContent: (
                <h2 className='centered-heading'>
                    {isEditMode ? t("EditSortCodeGroup") : t("AddSortCodeGroupName")}
                </h2>
            ),
            bodyContent: ({ value, handleValueChange, direction }) => (
                <div className={`${direction}`} style={{ width: '100%' }} >
                    {/* SortCode Group Name */}
                    <div className="form-row-item profile-full-width">
                        <div className="form-row-item-title">
                            {t('sortCodeGroupName')}<span className="required-asterisk">*</span>
                        </div>
                        <TextField
                            fullWidth
                            placeholder={t('sortCodeGroupName')}
                            value={value.sortCodeGroupName || ''}
                            onChange={(e) => handleValueChange({ ...value, sortCodeGroupName: e.target.value })}
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    height: '48px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    background: '#F5F5F6',
                                    border: '1px solid transparent',
                                    '&:focus-within': {
                                        border: '2px solid #304FFF',
                                    },
                                },
                            }}
                            inputProps={{
                                style: {
                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                },
                            }}
                        />
                        <RequiredError show={showValidation && !value?.sortCodeGroupName} />
                    </div>
                    {/* SortCode Group List Number */}
                    <div className="form-row-item profile-full-width">
                        <div className="form-row-item-title">
                            {t('SortCodeGroupListNumber')}<span className="required-asterisk">*</span>
                        </div>
                        <TextField
                            fullWidth
                            placeholder={t('SortCodeGroupListNumber')}
                            value={value.sortCodeGroupListNumber || ''}
                            onChange={(e) => handleValueChange({ ...value, sortCodeGroupListNumber: e.target.value })}
                            variant="standard"
                            type="number"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    height: '48px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    background: '#F5F5F6',
                                    border: '1px solid transparent',
                                    '&:focus-within': {
                                        border: '2px solid #304FFF',
                                    },
                                },
                            }}
                            inputProps={{
                                style: {
                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                },
                            }}
                        />
                        <RequiredError show={showValidation && !value?.sortCodeGroupListNumber} />
                    </div>
                    {/* Profit Name */}
                    <div className="form-row-item profile-full-width">
                        <div className="form-row-item-title">
                            {t('ProfitName')}
                        </div>
                        <TextField
                            fullWidth
                            placeholder={t('ProfitName')}
                            value={value.profitName || ''}
                            onChange={(e) => handleValueChange({ ...value, profitName: e.target.value })}
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    height: '48px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    background: '#F5F5F6',
                                    border: '1px solid transparent',
                                    '&:focus-within': {
                                        border: '2px solid #304FFF',
                                    },
                                },
                            }}
                            inputProps={{
                                style: {
                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                },
                            }}
                        />
                    </div>
                    <div className="form-row-item profile-full-width">
                        <div className="form-row-item-title">
                            {t('groupType')}<span className="required-asterisk">*</span>
                        </div>
                        <Select
                            placeholder={t("groupType")}
                            options={Object.entries(Constants.SortCodeGroupCategoryName).map(
                                ([value, label]) => ({
                                    value: Number(value), // 1 או 2
                                    label,                // "הכנסות" / "הוצאות"
                                })
                            )}
                            value={Object.entries(Constants.SortCodeGroupCategoryName)
                                .map(([value, label]) => ({ value: Number(value), label }))
                                .find((option) => option.value === value.category) || null}
                            onChange={(selectedOption) =>
                                handleValueChange({
                                    ...value,
                                    category: selectedOption?.value ?? null,
                                })
                            }
                            {...selectCommonProps}
                        />
                    </div>
                </div>
            ),
        },
        PalCode: {
            headerContent: (
                <h2 className='centered-heading'>
                    {isEditMode ? t("EditBalanceClassification") : t("NewBalanceClassification")}
                </h2>
            ),
            bodyContent: ({ value, handleValueChange, direction }) => (
                <div className={`${direction}`} style={{ width: '100%' }} >
                    {/* Balance Classification Type */}
                    <div className="form-row-item profile-full-width">
                        <div className="form-row-item-title">
                            {t('BalanceClassificationType')}<span className="required-asterisk">*</span>
                        </div>
                        <Select
                            placeholder={t('BalanceClassificationType')}
                            options={Constants.PalCodeType.map(type => ({
                                value: type,
                                label: t(type),
                            }))}
                            value={
                                Constants.PalCodeType.map(type => ({
                                    value: type,
                                    label: t(type),
                                })).find(option => option.value === value.palCodeType) || null
                            }
                            onChange={(selectedOption) => {
                                const newType = selectedOption?.value || '';
                                // Always update and trigger fetch, even if same value
                                handleValueChange({ ...value, palCodeType: newType, code6111Id: null });
                                // Trigger fetch directly if callback is provided
                                if (onPalCodeTypeChange && newType) {
                                    onPalCodeTypeChange(newType);
                                }
                            }}
                            {...selectCommonProps}
                        />
                        <RequiredError show={showValidation && !value?.palCodeType} />
                    </div>
                    {/* Balance Classification Name */}
                    <div className="form-row-item profile-full-width">
                        <div className="form-row-item-title">
                            {t('BalanceClassificationName')}<span className="required-asterisk">*</span>
                        </div>
                        <TextField
                            fullWidth
                            placeholder={t('BalanceClassificationName')}
                            value={value.palCodeId || ''}
                            onChange={(e) => handleValueChange({ ...value, palCodeId: e.target.value })}
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    height: '48px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    background: '#F5F5F6',
                                    border: '1px solid transparent',
                                    '&:focus-within': {
                                        border: '2px solid #304FFF',
                                    },
                                },
                            }}
                            inputProps={{
                                style: {
                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                },
                            }}
                        />
                        <RequiredError show={showValidation && !value?.palCodeId} />
                    </div>
                    {/* Balance Classification Category */}
                    <div className="form-row-item profile-full-width" style={{ marginTop: '30px' }}>
                        <div className="form-row-item-title">
                            {t('SelectBalanceClassificationCategory')}<span className="required-asterisk">*</span>
                        </div>
                        <Select
                            placeholder={t('SelectBalanceClassificationCategory')}
                            options={(formattedPalCodeCategoryOptions || []).map((item) => ({
                                name: item.palCodeCategoryId,
                                label: item.palCodeCategoryId,
                                value: item.palCodeCategoryId,
                            }))}
                            isSearchable={true}
                            value={(formattedPalCodeCategoryOptions || []).map((item) => ({
                                name: item.palCodeCategoryId,
                                label: item.palCodeCategoryId,
                                value: item.palCodeCategoryId,
                            })).find(item => item.value === value.palCodeCategory) || null}
                            onChange={(selectedOption) => handleValueChange({ ...value, palCodeCategory: selectedOption.value })}
                            {...selectCommonProps}
                        />
                        <RequiredError show={showValidation && !value?.palCodeCategory} />
                    </div>
                    {/* Code6111 Select - Only show when palCodeType is selected */}
                    {value.palCodeType && (
                        <div className="form-row-item profile-full-width" style={{ marginTop: '30px' }}>
                            <div className="form-row-item-title">
                                {t('Code6111')}
                            </div>
                            <Select
                                placeholder={t('SelectCode6111')}
                                options={(filteredCode6111Options || []).sort((a, b) => {
                                    const numA = Number(a.number) || 0;
                                    const numB = Number(b.number) || 0;
                                    return numA - numB;
                                }).map((item) => ({
                                    label: `${item.number} - ${item.name}`,
                                    value: item.id,
                                    number: item.number,
                                    name: item.name,
                                    title: item.title,
                                }))}
                                isSearchable={true}
                                value={(filteredCode6111Options || []).sort((a, b) => {
                                    const numA = Number(a.number) || 0;
                                    const numB = Number(b.number) || 0;
                                    return numA - numB;
                                }).map((item) => ({
                                    label: `${item.number} - ${item.name}`,
                                    value: item.id,
                                })).find(item => item.value === ( value.Code6111Id)) || null}
                                onChange={(selectedOption) => handleValueChange({ ...value, code6111Id: selectedOption?.value || null })}
                                {...selectCommonProps}
                                isClearable={true}
                            />
                        </div>
                    )}
                    {/* Order Number */}
                    <div className="form-row-item profile-full-width">
                        <Tooltip title={t('SerialNumberInTrialBalancePage')} arrow placement="top">
                            <div className="form-row-item-title">
                                {t('SerialNumber')}
                            </div>
                        </Tooltip>
                        <TextField
                            fullWidth
                            placeholder={t('SerialNumber')}
                            value={value.orderNumber || ''}
                            onChange={(e) => handleValueChange({ ...value, orderNumber: e.target.value })}
                            variant="standard"
                            type="number"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    height: '48px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    background: '#F5F5F6',
                                    border: '1px solid transparent',
                                    '&:focus-within': {
                                        border: '2px solid #304FFF',
                                    },
                                },
                            }}
                            inputProps={{
                                style: {
                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                },
                            }}
                        />
                    </div>
                </div>
            ),
        },
        PalCodeCategory: {
            headerContent: (
                <h2 className='centered-heading'>
                    {isEditMode ? t("EditBalanceItem") : t("NewBalanceItem")}
                </h2>
            ),
            bodyContent: ({ value, handleValueChange, direction }) => (
                <div className={`${direction}`} style={{ width: '100%' }} >
                    {/* Balance Classification Category */}
                    <div className="form-row-item profile-full-width">
                        <div className="form-row-item-title">
                            {t('BalanceClassificationCategory')}<span className="required-asterisk">*</span>
                        </div>
                        <TextField
                            fullWidth
                            placeholder={t('BalanceClassificationCategory')}
                            value={value.palCodeCategoryId || ''}
                            onChange={(e) => handleValueChange({ ...value, palCodeCategoryId: e.target.value })}
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    height: '48px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    background: '#F5F5F6',
                                    border: '1px solid transparent',
                                    '&:focus-within': {
                                        border: '2px solid #304FFF',
                                    },
                                },
                            }}
                            inputProps={{
                                style: {
                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                },
                            }}
                        />
                        <RequiredError show={showValidation && !value?.palCodeCategoryId} />
                    </div>
                    {/* Serial Number */}
                    <div className="form-row-item profile-full-width">
                        <Tooltip title={t('SerialNumberInTrialBalancePage')} arrow placement="top">

                            <div className="form-row-item-title">
                                {t('SerialNumber')}<span className="required-asterisk">*</span>
                            </div>
                        </Tooltip>

                        <TextField
                            fullWidth
                            placeholder={t('SerialNumber')}
                            value={value.orderNumber || ''}
                            onChange={(e) => handleValueChange({ ...value, orderNumber: e.target.value })}
                            variant="standard"
                            type="number"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    height: '48px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    background: '#F5F5F6',
                                    border: '1px solid transparent',
                                    '&:focus-within': {
                                        border: '2px solid #304FFF',
                                    },
                                },
                            }}
                            inputProps={{
                                style: {
                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                },
                            }}
                        />
                        <RequiredError show={showValidation && !value?.orderNumber} />
                    </div>
                    {/* Accounting Group */}
                    <div className="form-row-item profile-full-width" style={{ marginTop: '30px' }}>
                        <div className="form-row-item-title">
                            {t('SelectAccountingGroup')}<span className="required-asterisk">*</span>
                        </div>
                        <Select
                            placeholder={t('SelectAccountingGroup')}
                            options={formattedPalCodeAccountingGroupOptions.map((item) => ({
                                name: item.accountingGroupName,
                                label: item.accountingGroupName,
                                value: item.accountingGroupName,
                            }))}
                            isSearchable={true}
                            value={formattedPalCodeAccountingGroupOptions.map((item) => ({
                                name: item.accountingGroupName,
                                label: item.accountingGroupName,
                                value: item.accountingGroupName,
                            })).find(item => item.value === value.accountingGroup) || null}
                            onChange={(selectedOption) => handleValueChange({ ...value, accountingGroup: selectedOption.value })}
                            {...selectCommonProps}
                        />
                        <RequiredError show={showValidation && !value?.accountingGroup} />
                    </div>
                </div>
            ),
        },
        AccountingGroup: {
            headerContent: (
                <h2 className='centered-heading'>
                    {isEditMode ? t("EditAccountingGroup") : t("NewAccountingGroup")}
                </h2>
            ),
            bodyContent: ({ value, handleValueChange, direction }) => (
                <div className={`${direction}`} style={{ width: '100%' }} >
                    {/* Attribute */}
                    <div className="form-row-item profile-full-width">
                        <div className="form-row-item-title">
                            {t('Attribute')}<span className="required-asterisk">*</span>
                        </div>
                        <Select
                            placeholder={t('Attribute')}
                            options={Constants.PalCodeClassification.map(type => ({
                                value: type,
                                label: t(type),
                            }))}
                            value={
                                Constants.PalCodeClassification.map(type => ({
                                    value: type,
                                    label: t(type),
                                })).find(option => option.value === value.accountingGroupClassifaction) || null
                            }
                            onChange={(selectedOption) => handleValueChange({ ...value, accountingGroupClassifaction: selectedOption.value })}
                            {...selectCommonProps}
                        />
                        <RequiredError show={showValidation && !value?.accountingGroupClassifaction} />
                    </div>
                    {/* Accounting Group Name */}
                    <div className="form-row-item profile-full-width">
                        <div className="form-row-item-title">
                            {t('AccountingGroup')}<span className="required-asterisk">*</span>
                        </div>
                        <TextField
                            fullWidth
                            placeholder={t('AccountingGroup')}
                            value={value.accountingGroupName || ''}
                            onChange={(e) => handleValueChange({ ...value, accountingGroupName: e.target.value })}
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    height: '48px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    background: '#F5F5F6',
                                    border: '1px solid transparent',
                                    '&:focus-within': {
                                        border: '2px solid #304FFF',
                                    },
                                },
                            }}
                            inputProps={{
                                style: {
                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                },
                            }}
                        />
                        <RequiredError show={showValidation && !value?.accountingGroupName} />
                    </div>
                    {/* Serial Number */}
                    <div className="form-row-item profile-full-width">
                        <Tooltip title={t('SerialNumberInTrialBalancePage')} arrow placement="top">

                            <div className="form-row-item-title">
                                {t('SerialNumber')}<span className="required-asterisk">*</span>
                            </div>
                        </Tooltip>
                        <TextField
                            fullWidth
                            placeholder={t('SerialNumber')}
                            value={value.orderNumber || ''}
                            onChange={(e) => handleValueChange({ ...value, orderNumber: e.target.value })}
                            variant="standard"
                            type="number"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    height: '48px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    background: '#F5F5F6',
                                    border: '1px solid transparent',
                                    '&:focus-within': {
                                        border: '2px solid #304FFF',
                                    },
                                },
                            }}
                            inputProps={{
                                style: {
                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                },
                            }}
                        />
                        <RequiredError show={showValidation && !value?.orderNumber} />
                    </div>
                </div>
            ),
        },
    };

    const { headerContent, bodyContent } = contentConfig[contentKey] || {};

    if (!isPopupOpen) return null;

    return (
        <>
            <PCNDocumentationModal
                open={showPcnDocumentation}
                onClose={() => setShowPcnDocumentation(false)}
                direction={direction}
            />

            <Dialog
                open={isPopupOpen}
                onClose={handleClose}
                dir={direction}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.16)',
                        // Paper scrolls; content stays overflow-visible so in-place
                        // select menus are not clipped mid-row.
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        px: 3,
                        py: 2,
                        m: 0,
                        borderBottom: '1px solid #ECECEF',
                        flexShrink: 0,
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            flex: 1,
                            fontSize: '1.15rem',
                            fontWeight: 600,
                            color: '#1A1A1A',
                            '& h2': { margin: 0, fontSize: 'inherit', fontWeight: 'inherit' },
                        }}
                    >
                        {headerContent}
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        aria-label={t('close')}
                        sx={{ color: '#6B6B6B' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        px: 3,
                        py: 3,
                        borderColor: '#ECECEF',
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        // Raise the row that has an open menu so it paints over
                        // the fields below (PCN, etc.).
                        '& .form-row-item:has(.accountant-select__menu)': {
                            zIndex: 5,
                            position: 'relative',
                        },
                    }}
                >
                    {typeof bodyContent === 'function'
                        ? bodyContent({ value, handleValueChange, direction })
                        : bodyContent}
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        gap: 1.5,
                        borderTop: '1px solid #ECECEF',
                    }}
                >
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            px: 3,
                            py: 1,
                            color: '#4A4A4A',
                            borderColor: '#D5D5DC',
                            '&:hover': { borderColor: '#B9B9C4', background: '#F5F5F6' },
                        }}
                    >
                        {t('close')}
                    </Button>
                    <Button
                        onClick={handleSaveChange}
                        variant="contained"
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            px: 3,
                            py: 1,
                            boxShadow: 'none',
                            background: isSaveDisabled
                                ? '#B9C0F5'
                                : 'var(--Foundation-Blue-Normal, #304FFF)',
                            '&:hover': {
                                boxShadow: 'none',
                                background: isSaveDisabled ? '#B9C0F5' : '#2540DB',
                            },
                        }}
                    >
                        {t('save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AccountantDetailsModals;
